import {
  Component,
  Input,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Observable, Subject, Subscription, forkJoin, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { LocationRepository } from "../../../location/location.repository";
import { LocationGroupRepository } from "../../../location-group/location-group.repository";
import { Location } from "../../../location/location.model";
import { LocationGroup } from "../../../location-group/location-group.model";
import { locationSelectDefaultOptions } from "../location-select/location-select-default-options.const";
import { locationKind } from "app/location/location-kind.const";
import { AutoCompleteSelectEvent, AutoComplete } from "primeng/autocomplete";
import { LocationIconComponent } from "../../location-icon/location-icon.component";
import { AutocompleteEmptyMessageComponent } from "../autocomplete-empty-message/autocomplete-empty-message.component";
import { FormsModule, NgModel } from "@angular/forms";

@Component({
  selector: "app-location-or-group-select",
  styleUrls: ["./location-or-group-select.component.scss"],
  templateUrl: "./location-or-group-select.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LocationIconComponent,
    AutoComplete,
    FormsModule,
    AutocompleteEmptyMessageComponent,
  ],
})
export class LocationOrGroupSelectComponent implements OnDestroy {
  private locationRepository = inject(LocationRepository);
  private locationGroupRepository = inject(LocationGroupRepository);

  public readonly selectionChange = output<Location | LocationGroup>();
  @Input() public selection: Location | LocationGroup = null;
  @Input() public searchQuery: string;
  @Input() public locationKindFilter = "";
  @Input() public locationKindFilterOptions = locationSelectDefaultOptions;
  public readonly appendTo = input<any>(undefined);
  public readonly required = input(false);
  public readonly showGroups = input(true);
  public readonly filterGroupsByKind = input(false);

  public locationKind = locationKind;
  public subscription = new Subscription();
  public suggestions: (Location | LocationGroup)[] = [];
  public readonly loading = signal(false);

  private searchTerm = new Subject<{ query: string; kindFilter: string }>();
  private readonly searchModel = viewChild(NgModel);

  public constructor() {
    this.subscription.add(
      this.searchTerm
        .pipe(
          switchMap(({ query, kindFilter }) =>
            forkJoin({
              locations: this.locationRepository
                .search(query, kindFilter)
                .pipe(catchError(() => of([] as Location[]))),
              locationGroups: this.searchLocationGroups(query, kindFilter),
            }),
          ),
        )
        .subscribe(({ locations, locationGroups }) => {
          // Groups lead the list: a location search resolves a country name to
          // its code, so a term like "china" returns every port in the country
          // and would push the group of the same name off the visible list.
          this.suggestions = [...locationGroups, ...locations];
          this.loading.set(false);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public isLocationGroup(
    option: Location | LocationGroup,
  ): option is LocationGroup {
    return option instanceof LocationGroup;
  }

  public getName(option: Location | LocationGroup): string {
    return this.isLocationGroup(option) ? option.name : option.getName();
  }

  public getKind(option: Location | LocationGroup): string {
    return this.isLocationGroup(option) ? null : option.getKind();
  }

  public getCode(option: Location | LocationGroup): string {
    return this.isLocationGroup(option) ? null : option.getCode();
  }

  // Inactive locations stay in the list but cannot be picked. Groups are always
  // selectable — they carry no active flag of their own.
  public isSelectable(option: Location | LocationGroup): boolean {
    return this.isLocationGroup(option) || option.active;
  }

  public onSelectionChange(event: AutoCompleteSelectEvent): void {
    if (!this.isSelectable(event.value)) {
      event.originalEvent.preventDefault();
      event.originalEvent.stopPropagation();

      return;
    }

    this.selection = event.value;
    this.selectionChange.emit(event.value);
    this.searchQuery = "";
  }

  // The control is required, so once it has been touched an empty value paints
  // it invalid. Callers that clear it after a successful save reset that.
  public resetTouchedState(): void {
    const control = this.searchModel()?.control;
    control?.markAsUntouched();
    control?.markAsPristine();
  }

  public getPlaceholder(): string {
    if (this.selection && !!this.getName(this.selection)) {
      return this.getName(this.selection);
    }

    return "Please select location or group";
  }

  public setLocationKindFilter(filter: string): void {
    this.locationKindFilter = filter;
  }

  public search(event: { originalEvent: KeyboardEvent; query: string }): void {
    const kindFilter =
      this.locationKindFilter ||
      (this.locationKindFilterOptions ?? locationSelectDefaultOptions).join(
        ",",
      );

    // new array reference opens the overlay so the loading message is visible
    this.suggestions = [];
    this.loading.set(true);
    this.searchTerm.next({ query: event.query, kindFilter });
  }

  private searchLocationGroups(
    query: string,
    kindFilter: string,
  ): Observable<LocationGroup[]> {
    if (!this.showGroups()) {
      return of([]);
    }

    return this.locationGroupRepository.search(query).pipe(
      map((locationGroups) =>
        this.filterGroupsByKind()
          ? locationGroups.filter((locationGroup) =>
              kindFilter.split(",").includes(locationGroup.kind),
            )
          : locationGroups,
      ),
      catchError(() => of([] as LocationGroup[])),
    );
  }
}
